// frontend/src/services/userService.js
import axiosInstance from './axiosConfig';

/**
 * Fetches users whose status is 'pending_approval'. (Admin only)
 * @returns {Promise<Array>} A promise resolving to an array of pending user objects.
 */
export const getPendingUsers = async () => {
    try {
        const response = await axiosInstance.get('/users/pending');
        return response.data || [];
    } catch (error) {
        console.error("API Error fetching pending users:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to fetch pending users');
    }
};

/**
 * Approves a user by setting their status to 'active'. (Admin only)
 * @param {number|string} userId - The ID of the user to approve.
 * @returns {Promise<object>} A promise resolving to the backend response.
 */
export const approveUser = async (userId) => {
    try {
        const response = await axiosInstance.patch(`/users/${userId}/approve`);
        return response.data;
    } catch (error) {
        console.error(`API Error approving user ${userId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to approve user');
    }
};

/**
 * Rejects (deletes) a pending user registration. (Admin only)
 * @param {number|string} userId - The ID of the user to reject.
 * @returns {Promise<void>} A promise that resolves on successful deletion.
 */
export const rejectUser = async (userId) => {
    try {
        await axiosInstance.delete(`/users/${userId}/reject`);
        // No data expected back on successful DELETE (204)
    } catch (error) {
        console.error(`API Error rejecting user ${userId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to reject user');
    }
};

/**
 * Fetches all users. (Admin only - Optional)
 * @returns {Promise<Array>} A promise resolving to an array of all user objects.
 */
export const getAllUsers = async () => {
    try {
        const response = await axiosInstance.get('/users');
        return response.data || [];
    } catch (error) {
        console.error("API Error fetching all users:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to fetch all users');
    }
};

/**
 * Assigns an account to a user. (Admin only)
 * @param {number|string} userId - The ID of the user.
 * @param {number|string} accountId - The ID of the account to assign.
 * @returns {Promise<object>} A promise resolving to the backend response (updated user).
 */
export const assignAccountToUser = async (userId, accountId) => {
    try {
        // The backend route is PUT /users/:userId/assign-account
        const response = await axiosInstance.put(`/users/${userId}/assign-account`, { accountId });
        return response.data;
    } catch (error) {
        console.error(`API Error assigning account ${accountId} to user ${userId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to assign account to user');
    }
};

// New function to update the logged-in user's username
export const updateMyUsername = async (usernameData) => {
    // usernameData should be an object, e.g., { newUsername: "thenewusername" }
    try {
        const response = await axiosInstance.put('/users/me/username', usernameData);
        return response.data; // Contains message and updated user object with new token (cookie handled by browser)
    } catch (error) {
        console.error('Error in updateMyUsername service:', error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to update username');
    }
};

// New function to change the logged-in user's password
export const changeMyPassword = async (passwordData) => {
    // passwordData should be an object, e.g., { currentPassword: "old", newPassword: "new" }
    try {
        const response = await axiosInstance.put('/users/me/password', passwordData);
        return response.data; // Contains success message
    } catch (error) {
        console.error('Error in changeMyPassword service:', error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to change password');
    }
};

/**
 * Creates a new user. (Admin only)
 * @param {object} userData - Object containing username, password, role, and accountId.
 * @returns {Promise<object>} A promise resolving to the created user object.
 */
export const createUser = async (userData) => {
    try {
        const response = await axiosInstance.post('/users', userData);
        return response.data;
    } catch (error) {
        console.error("API Error creating user:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to create user');
    }
};

/**
 * Deletes a user by ID. (Admin only)
 * @param {number|string} userId - The ID of the user to delete.
 * @returns {Promise<void>} A promise that resolves on successful deletion.
 */
export const deleteUser = async (userId) => {
    try {
        await axiosInstance.delete(`/users/${userId}`);
    } catch (error) {
        console.error(`API Error deleting user ${userId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to delete user');
    }
};

/**
 * Updates a user's role. (Admin only)
 * @param {number|string} userId - The ID of the user.
 * @param {string} role - The new role ('Member' or 'Administrator').
 * @returns {Promise<object>} A promise resolving to the backend response.
 */
export const updateUserRole = async (userId, role) => {
    try {
        const response = await axiosInstance.put(`/users/${userId}/role`, { role });
        return response.data;
    } catch (error) {
        console.error(`API Error updating role for user ${userId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to update user role');
    }
};